import type { IntlShape } from 'react-intl-next';
import { defineMessages } from 'react-intl-next';
import memoizeOne from 'memoize-one';

import type { MediaOptions } from '@atlaskit/editor-plugins/media/types';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { MediaViewerExtensions } from '@atlaskit/media-viewer';

import { getMediaFeatureFlags } from '@confluence/fabric-media-support/entry-points/mediaFeatureFlags.ts';

const i18n = defineMessages({
	invalidAltText: {
		id: 'editor-features.alt-text.invalid',
		defaultMessage:
			// eslint-disable-next-line no-restricted-syntax
			"We aren’t able to accept every possible character in Alt text. All Unicode letters and numbers are OK, but these are the only acceptable symbols: , ' . - _ ( ).",
		description:
			"Error message to explain that alt text for an image on a page can only contain any Unicode alphanumerics along with any characters from the following symbol set: , ' . - _ ( )",
	},
});

export const getMediaOptions = memoizeOne(
	({
		mediaProvider,
		ssrMediaProvider,
		intl,
		featureFlags,
		mediaViewerExtensions,
	}: {
		mediaProvider: Promise<MediaProvider> | undefined;
		ssrMediaProvider?: MediaProvider;
		intl: IntlShape;
		featureFlags: Parameters<typeof getMediaFeatureFlags>[0];
		mediaViewerExtensions?: MediaViewerExtensions;
	}): MediaOptions => {
		// IMPORTANT: If you change the code below, please also update the
		// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/mediaPluginOptions/mediaPluginOptions.ts
		return {
			provider: mediaProvider,
			syncProvider: ssrMediaProvider,
			allowMediaSingle: true,
			allowResizing: true,
			allowResizingInTables: true,
			allowLinking: true,
			allowAltTextOnImages: true,
			allowCaptions: true,
			allowMediaInlineImages: true,
			allowPixelResizing: true,
			altTextValidator: (text: string) => {
				const storageSupports = /^([\p{L}\p{N},'\.\s\-_\(\)]|&amp;[0-9]{2};)*$/u;

				if (!storageSupports.test(text)) {
					return [intl.formatMessage(i18n.invalidAltText)];
				}

				return [];
			},
			featureFlags: getMediaFeatureFlags(featureFlags),
			mediaViewerExtensions,
		};
	},
);
